import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  IconButton,
  Grid,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import {
  CheckCircleOutline,
  AutoFixHigh,
  ContentCopy,
  DeleteOutline,
  ErrorOutline,
  DoneAll,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import axios from "axios";
import API_BASE_URL from "../config";

interface GrammarError {
  type: string;
  message: string;
  suggestion: string;
  position: number;
  context: string;
}

export default function GrammarChecker(): JSX.Element {
  const [inputText, setInputText] = useState("");
  const [correctedText, setCorrectedText] = useState("");
  const [errors, setErrors] = useState<GrammarError[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const handleCheck = async () => {
    if (!inputText.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/grammar-correct`, {
        text: inputText,
      }, { timeout: 90000 });
      if (response.data) {
        setCorrectedText(response.data.corrected_text);
        setErrors(response.data.errors || []);
        setHasChecked(true);
      }
    } catch (error) {
      console.error("Grammar check failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFixAll = () => {
    setInputText(correctedText);
    setErrors([]);
    setHasChecked(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(hasChecked ? correctedText : inputText);
  };

  const handleClear = () => {
    setInputText("");
    setCorrectedText("");
    setErrors([]);
    setHasChecked(false);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            trackingTight: "-0.02em",
            color: isDarkMode ? "white" : "text.primary",
          }}
        >
          Grammar{" "}
          <span style={{ color: isDarkMode ? "#4b5563" : "#3b82f6" }}>
            Check
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? "#9ca3af" : "text.secondary",
            fontWeight: 400,
            maxWidth: 600,
          }}
        >
          Polish your writing with advanced grammar, spelling, and style
          suggestions.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Editor */}
        <Grid
          item
          xs={12}
          md={errors.length > 0 ? 8 : 12}
          sx={{ transition: "all 0.3s" }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              display: "flex",
              flexDirection: "column",
              minHeight: 500,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Stack direction="row" spacing={1}>
                <Chip
                  label="English (US)"
                  size="small"
                  sx={{
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                    color: isDarkMode ? "#9ca3af" : "text.secondary",
                    fontSize: "11px",
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <IconButton
                  size="small"
                  onClick={handleClear}
                  sx={{ color: isDarkMode ? "#4b5563" : "text.secondary" }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={{ color: isDarkMode ? "#4b5563" : "text.secondary" }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            <Box sx={{ p: 3, flexGrow: 1 }}>
              <TextField
                fullWidth
                multiline
                placeholder="Type or paste your text here..."
                value={inputText}
                onChange={(e) => {
                  setInputText(e.target.value);
                  if (hasChecked) setHasChecked(false);
                }}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontFamily: "inherit",
                    fontSize: "16px",
                    lineHeight: 1.7,
                    color: isDarkMode ? "white" : "text.primary",
                    alignItems: "flex-start",
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#4b5563" : "text.secondary" }}
              >
                {inputText.split(/\s+/).filter((x) => x).length} words
              </Typography>
              <Button
                variant="contained"
                onClick={handleCheck}
                disabled={loading || !inputText.trim()}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckCircleOutline />
                  )
                }
                sx={{
                  borderRadius: "12px",
                  bgcolor: isDarkMode ? "white" : "primary.main",
                  color: isDarkMode ? "black" : "white",
                  textTransform: "none",
                  px: 4,
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: isDarkMode ? "#e5e7eb" : "primary.dark",
                  },
                  "&.Mui-disabled": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    color: isDarkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.25)",
                  },
                }}
              >
                Check Text
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Suggestions Sidebar */}
        {errors.length > 0 && (
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: isDarkMode ? "white" : "text.primary",
                    fontWeight: 600,
                  }}
                >
                  Suggestions ({errors.length})
                </Typography>
                <Button
                  size="small"
                  startIcon={<DoneAll />}
                  onClick={handleFixAll}
                  sx={{
                    color: "#3b82f6",
                    textTransform: "none",
                    fontSize: "13px",
                  }}
                >
                  Apply All
                </Button>
              </Box>

              <Box
                sx={{
                  maxHeight: 600,
                  overflowY: "auto",
                  pr: 1,
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.1)",
                    borderRadius: "10px",
                  },
                }}
              >
                {errors.map((err, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: "16px",
                      bgcolor: isDarkMode ? "#0f0f0f" : "#f9fafb",
                      border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "rgba(59,130,246,0.3)",
                        transform: "translateX(-4px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
                      <ErrorOutline
                        sx={{ color: "#ef4444", fontSize: "18px", mt: 0.2 }}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? "#f3f4f6" : "text.primary",
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        >
                          {err.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDarkMode ? "#6b7280" : "text.secondary",
                            display: "block",
                            fontStyle: "italic",
                          }}
                        >
                          "...{err.context}..."
                        </Typography>
                      </Box>
                    </Box>
                    <Divider
                      sx={{
                        borderColor: isDarkMode
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(0,0,0,0.05)",
                        my: 1.5,
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDarkMode ? "#4b5563" : "text.secondary",
                          }}
                        >
                          Suggestion:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#10b981", fontWeight: 600 }}
                        >
                          {err.suggestion || "Remove"}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        sx={{
                          color: "#10b981",
                          bgcolor: "rgba(16,185,129,0.1)",
                          "&:hover": { bgcolor: "rgba(16,185,129,0.2)" },
                        }}
                      >
                        <AutoFixHigh fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Stack>
          </Grid>
        )}

        {/* Empty State / Success */}
        {hasChecked && errors.length === 0 && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 6,
                textAlign: "center",
                borderRadius: "24px",
                bgcolor: "rgba(16,185,129,0.05)",
                border: "1px dashed rgba(16,185,129,0.2)",
              }}
            >
              <CheckCircleOutline
                sx={{ color: "#10b981", fontSize: 48, mb: 2 }}
              />
              <Typography
                variant="h6"
                sx={{ color: isDarkMode ? "white" : "text.primary", mb: 1 }}
              >
                Perfect! No errors found.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? "#9ca3af" : "text.secondary" }}
              >
                Your writing is clear and concise.
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
